import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import {
  DocTemplate, DocRole, PlacedField, FieldType, uid,
} from '../lib/onboardingDocsStore';
import { onboardingService } from '../services/onboardingService';
import '../styles/DocumentTemplateEditor.css';

// Worker served same-origin from /public (copied from pdfjs-dist at build time)
// so it isn't blocked by the app's Content-Security-Policy (script-src 'self').
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const ICONS: Record<FieldType, React.ReactNode> = {
  signature: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17c3 0 3-8 6-8s3 8 6 8 3-4 6-4"/><path d="M14 3l7 7"/></svg>,
  radio:     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="7" r="2"/><line x1="11" y1="7" x2="20" y2="7"/><circle cx="6" cy="17" r="2"/><line x1="11" y1="17" x2="20" y2="17"/></svg>,
  checkbox:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 12l3 3 5-6"/></svg>,
  text:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>,
  date:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  select:    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
};
const FIELD_META: Record<FieldType, { label: string; w: number; h: number }> = {
  signature: { label: 'Full Signature', w: 0.18, h: 0.05 },
  radio:     { label: 'Radio buttons',  w: 0.16, h: 0.05 },
  checkbox:  { label: 'Checkbox',        w: 0.04, h: 0.03 },
  text:      { label: 'Text Field',      w: 0.20, h: 0.04 },
  date:      { label: 'Date Field',      w: 0.14, h: 0.04 },
  select:    { label: 'Select Box',      w: 0.16, h: 0.04 },
};
const FIELD_ORDER: FieldType[] = ['signature', 'radio', 'checkbox', 'text', 'date', 'select'];
const ROLE_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
const AUTO_POP_OPTIONS = ['', 'Client Name', 'Client Email', 'Company Name', 'Representative', 'Today'];

const DocumentTemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tpl, setTpl] = useState<DocTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRoleId, setActiveRoleId] = useState<string>('');
  const [saved, setSaved] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [renderTick, setRenderTick] = useState(0); // bumps when pages are (re)rendered
  const [editingRole, setEditingRole] = useState<DocRole | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const pdfDocRef = useRef<any>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const pageSizesRef = useRef<{ w: number; h: number }[]>([]);

  // Load the template (with its PDF) from the DB.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) { setLoading(false); return; }
      try {
        const t = await onboardingService.getTemplate(id);
        if (cancelled) return;
        setTpl(t ? { ...t, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() } : null);
      } catch (err) {
        console.error('Failed to load template:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (tpl && !activeRoleId && tpl.roles[0]) setActiveRoleId(tpl.roles[0].id);
  }, [tpl, activeRoleId]);

  // Render the PDF into stacked canvases.
  const renderPdf = useCallback(async () => {
    if (!tpl?.pdfDataUrl || !canvasWrapRef.current) return;
    const wrap = canvasWrapRef.current;
    wrap.querySelectorAll('.dte-page-canvas').forEach(c => c.remove());

    const bytes = Uint8Array.from(atob(tpl.pdfDataUrl.split(',')[1]), c => c.charCodeAt(0));
    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    pdfDocRef.current = pdf;
    pageSizesRef.current = [];

    for (let p = 1; p <= pdf.numPages; p++) {
      const page = await pdf.getPage(p);
      const viewport = page.getViewport({ scale: 1.4 * zoom });
      const canvas = document.createElement('canvas');
      canvas.className = 'dte-page-canvas';
      canvas.dataset.page = String(p);
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d')!;
      // hosts the field overlay (positioned relative)
      const holder = document.createElement('div');
      holder.className = 'dte-page-holder';
      holder.dataset.page = String(p);
      holder.style.width = `${viewport.width}px`;
      holder.style.height = `${viewport.height}px`;
      holder.appendChild(canvas);
      wrap.appendChild(holder);
      pageSizesRef.current[p] = { w: viewport.width, h: viewport.height };
      await page.render({ canvasContext: ctx, viewport } as any).promise;
    }
    // re-anchor overlays after (re)render — holders now exist in the DOM
    setRenderTick(t => t + 1);
  }, [tpl?.pdfDataUrl, zoom]);

  useEffect(() => { renderPdf(); /* eslint-disable-next-line */ }, [tpl?.pdfDataUrl, zoom]);

  if (loading) {
    return <div className="dte-page"><div className="dte-empty">Loading…</div></div>;
  }
  if (!tpl) {
    return (
      <div className="dte-page">
        <div className="dte-empty">Template not found. <button onClick={() => navigate('/onboarding/documents')}>Back to Documents</button></div>
      </div>
    );
  }

  const update = (patch: Partial<DocTemplate>) => { setTpl({ ...tpl, ...patch }); setSaved(false); };
  const activeRole = tpl.roles.find(r => r.id === activeRoleId) ?? tpl.roles[0];

  const handleSave = async () => {
    try {
      await onboardingService.updateTemplate({ id: tpl.id, name: tpl.name, description: tpl.description, pdfDataUrl: tpl.pdfDataUrl, roles: tpl.roles, fields: tpl.fields });
      setSaved(true);
    } catch (err: any) {
      console.error('Failed to save template:', err);
      alert(`Failed to save template: ${err?.message ?? err}`);
    }
  };

  // Replace the document's PDF. Because field coordinates are tied to the old
  // pages, all placed fields are cleared when the file changes.
  const onReplaceDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF files are allowed.');
      return;
    }
    if (tpl.fields.length > 0 && !window.confirm('Replacing the document will remove all placed fields. Continue?')) return;
    const dataUrl: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    setEditingFieldId(null);
    update({ name: file.name, pdfDataUrl: dataUrl, fields: [] });
  };

  const addRole = () => {
    const color = ROLE_COLORS[tpl.roles.length % ROLE_COLORS.length];
    const role: DocRole = { id: uid(), name: `Role ${tpl.roles.length + 1}`, color };
    update({ roles: [...tpl.roles, role] });
    setActiveRoleId(role.id);
  };
  const saveRole = (r: DocRole) => { update({ roles: tpl.roles.map(x => x.id === r.id ? r : x) }); setEditingRole(null); };
  const removeRole = (roleId: string) => {
    if (tpl.roles.length <= 1) { alert('A template must have at least one role.'); return; }
    const count = tpl.fields.filter(f => f.roleId === roleId).length;
    if (count > 0 && !window.confirm(`Remove this role? Its ${count} field${count > 1 ? 's' : ''} will also be removed.`)) return;
    const nextRoles = tpl.roles.filter(r => r.id !== roleId);
    update({ roles: nextRoles, fields: tpl.fields.filter(f => f.roleId !== roleId) });
    if (activeRoleId === roleId) setActiveRoleId(nextRoles[0]?.id ?? '');
    setEditingRole(null);
  };

  // Drop a new field at the pointer location on a page.
  const onDropField = (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('field-type') as FieldType;
    if (!type || !activeRole) return;
    const holder = (e.target as HTMLElement).closest('.dte-page-holder') as HTMLElement | null;
    if (!holder) return;
    const page = Number(holder.dataset.page);
    const rect = holder.getBoundingClientRect();
    const meta = FIELD_META[type];
    const x = (e.clientX - rect.left) / rect.width - meta.w / 2;
    const y = (e.clientY - rect.top) / rect.height - meta.h / 2;
    const field: PlacedField = {
      id: uid(), type, roleId: activeRole.id, page,
      x: Math.max(0, Math.min(1 - meta.w, x)),
      y: Math.max(0, Math.min(1 - meta.h, y)),
      w: meta.w, h: meta.h,
      label: '',
      required: type !== 'checkbox',
      ...(type === 'select' || type === 'radio' ? { options: ['Option 1'] } : {}),
      ...(type === 'date' ? { dateFormat: 'MDY' as const } : {}),
      ...(type === 'text' ? { format: 'any' as const } : {}),
    };
    update({ fields: [...tpl.fields, field] });
    setEditingFieldId(field.id);
  };

  const removeField = (fid: string) => { update({ fields: tpl.fields.filter(f => f.id !== fid) }); setEditingFieldId(null); };
  const patchField = (fid: string, patch: Partial<PlacedField>) =>
    update({ fields: tpl.fields.map(f => f.id === fid ? { ...f, ...patch } : f) });

  // Resize a placed field via its bottom-right handle.
  const startResizeField = (e: React.MouseEvent, field: PlacedField) => {
    e.preventDefault();
    e.stopPropagation();
    const holder = (e.currentTarget as HTMLElement).closest('.dte-page-holder') as HTMLElement;
    const rect = holder.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY;
    const origW = field.w, origH = field.h;
    const onMove = (ev: MouseEvent) => {
      const nw = origW + (ev.clientX - startX) / rect.width;
      const nh = origH + (ev.clientY - startY) / rect.height;
      setTpl(prev => prev ? {
        ...prev,
        fields: prev.fields.map(f => f.id === field.id ? {
          ...f,
          w: Math.max(0.03, Math.min(1 - f.x, nw)),
          h: Math.max(0.02, Math.min(1 - f.y, nh)),
        } : f),
      } : prev);
      setSaved(false);
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Drag an already-placed field to reposition within its page.
  const startMoveField = (e: React.MouseEvent, field: PlacedField) => {
    e.preventDefault();
    e.stopPropagation();
    const holder = (e.currentTarget as HTMLElement).closest('.dte-page-holder') as HTMLElement;
    const rect = holder.getBoundingClientRect();
    const startX = e.clientX, startY = e.clientY;
    const origX = field.x, origY = field.y;
    const onMove = (ev: MouseEvent) => {
      const nx = origX + (ev.clientX - startX) / rect.width;
      const ny = origY + (ev.clientY - startY) / rect.height;
      setTpl(prev => prev ? {
        ...prev,
        fields: prev.fields.map(f => f.id === field.id ? {
          ...f,
          x: Math.max(0, Math.min(1 - f.w, nx)),
          y: Math.max(0, Math.min(1 - f.h, ny)),
        } : f),
      } : prev);
      setSaved(false);
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const roleColor = (rid: string) => tpl.roles.find(r => r.id === rid)?.color ?? '#94a3b8';

  return (
    <div className="dte-page">
      {/* Top bar */}
      <div className="dte-topbar">
        <button className="dte-back" onClick={() => navigate('/onboarding/documents')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          All Contracts
        </button>
        {renaming ? (
          <input
            className="dte-title-input"
            value={tpl.name}
            autoFocus
            onChange={e => update({ name: e.target.value })}
            onBlur={() => { if (!tpl.name.trim()) update({ name: 'Untitled document' }); setRenaming(false); }}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur(); }}
          />
        ) : (
          <button className="dte-title" title="Click to rename" onClick={() => setRenaming(true)}>
            {tpl.name}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
        )}
        <button className={`dte-save ${saved ? 'saved' : ''}`} onClick={handleSave}>{saved ? 'Saved' : 'Save Template'}</button>
      </div>

      <div className="dte-body">
        {/* Left panel */}
        <aside className="dte-panel">
          <input ref={replaceInputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={onReplaceDocument} />

          <div className="dte-panel-section">
            <div className="dte-panel-head">
              <span>Settings</span>
              <button className="dte-icon-danger" title="Delete template" onClick={() => setConfirmDelete(true)}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
              </button>
            </div>

            {/* Document card */}
            <div className="dte-doc-card">
              <span className="dte-doc-ic">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </span>
              <div className="dte-doc-info">
                <span className="dte-doc-name" title={tpl.name}>{tpl.name}</span>
                <span className="dte-doc-meta">{tpl.fields.length} field{tpl.fields.length === 1 ? '' : 's'} · {tpl.roles.length} role{tpl.roles.length === 1 ? '' : 's'}</span>
              </div>
            </div>

            <button className="dte-replace" onClick={() => replaceInputRef.current?.click()}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              Replace Document
            </button>

            <label className="dte-field-label">Description</label>
            <textarea className="dte-desc" placeholder="Add a short description…" value={tpl.description} onChange={e => update({ description: e.target.value })} rows={2} />
          </div>

          <div className="dte-panel-section">
            <div className="dte-panel-head">
              <span>Roles</span>
              <button className="dte-icon-btn" title="Add role" onClick={addRole}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
            </div>
            {tpl.roles.map(r => {
              const count = tpl.fields.filter(f => f.roleId === r.id).length;
              return (
                <div key={r.id} className={`dte-role ${activeRoleId === r.id ? 'active' : ''}`} onClick={() => setActiveRoleId(r.id)}>
                  <span className="dte-role-dot" style={{ background: r.color }} />
                  <span className="dte-role-name">{r.name}</span>
                  {count > 0
                    ? <span className="dte-role-count">{count}</span>
                    : <span className="dte-role-warn" title="No fields assigned yet">⚠</span>}
                  <button className="dte-role-edit" title="Edit role" onClick={e => { e.stopPropagation(); setEditingRole(r); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="dte-panel-section">
            <div className="dte-panel-head"><span>Field Types</span></div>
            <p className="dte-hint">Drag fields onto the contract for {activeRole?.name}</p>
            {FIELD_ORDER.map(type => (
              <div
                key={type}
                className="dte-field-tile"
                draggable
                onDragStart={e => e.dataTransfer.setData('field-type', type)}
              >
                <span className="dte-field-icon">{ICONS[type]}</span>
                {FIELD_META[type].label}
              </div>
            ))}
          </div>
        </aside>

        {/* PDF canvas */}
        <main className="dte-canvas-area">
          <div
            className="dte-canvas-scroll"
            onDragOver={e => e.preventDefault()}
            onDrop={onDropField}
          >
            <div className="dte-pages" ref={canvasWrapRef} />
            {/* Field overlays are portaled into page holders via absolute divs */}
            <FieldOverlays
              key={renderTick}
              fields={tpl.fields}
              roleColor={roleColor}
              selectedId={editingFieldId}
              onMove={startMoveField}
              onResize={startResizeField}
              onRemove={removeField}
              onSelect={setEditingFieldId}
            />
          </div>

          <div className="dte-zoombar">
            <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.15).toFixed(2)))} title="Zoom out">−</button>
            <span>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(2.5, +(z + 0.15).toFixed(2)))} title="Zoom in">+</button>
          </div>
        </main>
      </div>

      {editingRole && (
        <RoleModal role={editingRole} canDelete={tpl.roles.length > 1} onClose={() => setEditingRole(null)} onSave={saveRole} onDelete={() => removeRole(editingRole.id)} />
      )}

      {(() => {
        const field = tpl.fields.find(f => f.id === editingFieldId);
        if (!field) return null;
        return (
          <EditFieldModal
            field={field}
            roles={tpl.roles}
            onChangeType={(type) => patchField(field.id, { type, ...(type === 'select' || type === 'radio' ? { options: field.options ?? ['Option 1'] } : {}), ...(type === 'date' ? { dateFormat: field.dateFormat ?? 'MDY' } : {}), ...(type === 'text' ? { format: field.format ?? 'any' } : {}) })}
            onPatch={(patch) => patchField(field.id, patch)}
            onDelete={() => removeField(field.id)}
            onClose={() => setEditingFieldId(null)}
          />
        );
      })()}

      {confirmDelete && (
        <div className="dte-confirm-backdrop" onClick={() => setConfirmDelete(false)}>
          <div className="dte-confirm-card" onClick={e => e.stopPropagation()}>
            <div className="dte-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            </div>
            <h3 className="dte-confirm-title">Delete this template?</h3>
            <p className="dte-confirm-text"><strong>{tpl.name}</strong> and all its fields will be permanently removed. This action can’t be undone.</p>
            <div className="dte-confirm-actions">
              <button className="dte-confirm-cancel" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="dte-confirm-delete" onClick={async () => { try { await onboardingService.deleteTemplate(tpl.id); } catch (e) { console.error(e); } navigate('/onboarding/documents'); }}>Delete template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Overlays: rendered as absolutely-positioned children appended into each
   page holder via refs, so they track the canvas. */
const FieldOverlays: React.FC<{
  fields: PlacedField[];
  roleColor: (rid: string) => string;
  selectedId: string | null;
  onMove: (e: React.MouseEvent, f: PlacedField) => void;
  onResize: (e: React.MouseEvent, f: PlacedField) => void;
  onRemove: (id: string) => void;
  onSelect: (id: string) => void;
}> = ({ fields, roleColor, selectedId, onMove, onResize, onRemove, onSelect }) => (
  <>
    {fields.map(f => (
      <PortalField key={f.id} field={f} color={roleColor(f.roleId)} selected={selectedId === f.id}
        onMove={onMove} onResize={onResize} onRemove={onRemove} onSelect={onSelect} />
    ))}
  </>
);

/* Renders a single field box positioned within its page holder. */
const PortalField: React.FC<{
  field: PlacedField;
  color: string;
  selected: boolean;
  onMove: (e: React.MouseEvent, f: PlacedField) => void;
  onResize: (e: React.MouseEvent, f: PlacedField) => void;
  onRemove: (id: string) => void;
  onSelect: (id: string) => void;
}> = ({ field, color, selected, onMove, onResize, onRemove, onSelect }) => {
  const holder = document.querySelector(`.dte-page-holder[data-page="${field.page}"]`) as HTMLElement | null;
  if (!holder) return null;
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${field.x * 100}%`,
    top: `${field.y * 100}%`,
    width: `${field.w * 100}%`,
    height: `${field.h * 100}%`,
    borderColor: color,
    color,
    background: `${color}18`,
  };
  const caption = field.label?.trim() || FIELD_META[field.type].label;
  const el = (
    <div
      className={`dte-field-box dte-fb-${field.type} ${selected ? 'selected' : ''}`}
      style={style}
      title="Drag to move · click to edit"
      onMouseDown={e => { onSelect(field.id); onMove(e, field); }}
    >
      <span className="dte-field-tag" style={{ background: color }}>{caption}{field.required && <span className="dte-field-tag-req">*</span>}</span>
      <FieldPreview field={field} color={color} />
      <button className="dte-field-box-remove" title="Remove" onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onRemove(field.id); }}>×</button>
      <span className="dte-field-resize" onMouseDown={e => onResize(e, field)} title="Resize" />
    </div>
  );
  return createPortal(el, holder);
};

/* Mini representation of the field, scaled to fill its box. */
const FieldPreview: React.FC<{ field: PlacedField; color: string }> = ({ field, color }) => {
  const req = field.required ? <span className="dte-fp-req">*</span> : null;
  const caption = field.label?.trim();
  switch (field.type) {
    case 'signature':
      return (
        <div className="dte-fp dte-fp-sig">
          <svg viewBox="0 0 100 24" preserveAspectRatio="none" className="dte-fp-sigline"><path d="M2 18c8-14 14 4 22-2s10-10 18-4 12 8 20 2 14-8 16-6" fill="none" stroke={color} strokeWidth="2"/></svg>
          <span className="dte-fp-cap">Signature{req}</span>
        </div>
      );
    case 'checkbox':
      return <div className="dte-fp dte-fp-check"><span className="dte-fp-box" style={{ borderColor: color, background: field.checked ? color : 'transparent' }} />{req}</div>;
    case 'radio':
      return (
        <div className="dte-fp dte-fp-choices">
          {(field.options ?? ['Option 1']).slice(0, 4).map((o, i) => (
            <span key={i} className="dte-fp-choice"><span className="dte-fp-radio" style={{ borderColor: color }} />{o}</span>
          ))}
        </div>
      );
    case 'select':
      return (
        <div className="dte-fp dte-fp-input">
          <span className="dte-fp-text">{caption || (field.options?.[0] ?? 'Select…')}{req}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      );
    case 'date':
      return (
        <div className="dte-fp dte-fp-input">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span className="dte-fp-text">{caption || (field.dateFormat ?? 'MDY')}{req}</span>
        </div>
      );
    case 'text':
    default:
      return (
        <div className="dte-fp dte-fp-input">
          <span className="dte-fp-text">{caption || field.defaultText || 'Text'}{req}</span>
        </div>
      );
  }
};

/* ── Edit Field modal (per-type properties, matches the reference) ── */
const EditFieldModal: React.FC<{
  field: PlacedField;
  roles: DocRole[];
  onChangeType: (t: FieldType) => void;
  onPatch: (patch: Partial<PlacedField>) => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ field, roles, onChangeType, onPatch, onDelete, onClose }) => {
  const [typeOpen, setTypeOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const role = roles.find(r => r.id === field.roleId) ?? roles[0];
  const options = field.options ?? [];
  const setOption = (i: number, v: string) => onPatch({ options: options.map((o, j) => j === i ? v : o) });
  const addOption = () => onPatch({ options: [...options, `Option ${options.length + 1}`] });
  const removeOption = (i: number) => onPatch({ options: options.length > 1 ? options.filter((_, j) => j !== i) : options });

  return (
    <div className="dte-editpanel">
      <div className="dte-ep-head">
        <h3>Edit Field</h3>
        <button className="dte-ep-close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div className="dte-ep-body">
        {/* Field type selector */}
        <div className="dte-ep-select" onClick={() => setTypeOpen(o => !o)}>
          <span className="dte-ep-select-val"><span className="dte-ep-ic">{ICONS[field.type]}</span>{FIELD_META[field.type].label}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          {typeOpen && (
            <div className="dte-ep-menu" onClick={e => e.stopPropagation()}>
              {FIELD_ORDER.map(t => (
                <button key={t} className={`dte-ep-menu-item ${t === field.type ? 'sel' : ''}`} onClick={() => { onChangeType(t); setTypeOpen(false); }}>
                  <span className="dte-ep-ic">{ICONS[t]}</span>{FIELD_META[t].label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Assigned to Role */}
        <label className="dte-ep-label">Assigned to Role</label>
        <div className="dte-ep-select" onClick={() => setRoleOpen(o => !o)}>
          <span className="dte-ep-select-val"><span className="dte-ep-dot" style={{ background: role?.color }} />{role?.name}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          {roleOpen && (
            <div className="dte-ep-menu" onClick={e => e.stopPropagation()}>
              {roles.map(r => (
                <button key={r.id} className={`dte-ep-menu-item ${r.id === field.roleId ? 'sel' : ''}`} onClick={() => { onPatch({ roleId: r.id }); setRoleOpen(false); }}>
                  <span className="dte-ep-dot" style={{ background: r.color }} />{r.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Required */}
        <label className="dte-ep-check">
          <input type="checkbox" checked={!!field.required} onChange={e => onPatch({ required: e.target.checked })} />
          Required {field.required && <span className="dte-ep-asterisk">*</span>}
        </label>

        {/* Checkbox: Checked */}
        {field.type === 'checkbox' && (
          <label className="dte-ep-check">
            <input type="checkbox" checked={!!field.checked} onChange={e => onPatch({ checked: e.target.checked })} />
            Checked
          </label>
        )}

        {/* Date: Use Signing Date */}
        {field.type === 'date' && (
          <label className="dte-ep-check">
            <input type="checkbox" checked={!!field.useSigningDate} onChange={e => onPatch({ useSigningDate: e.target.checked })} />
            Use Signing Date
          </label>
        )}

        {/* Label (all except signature which has only Required, and checkbox which has none) */}
        {field.type !== 'signature' && field.type !== 'checkbox' && (
          <>
            <label className="dte-ep-label">Label {field.required && <span className="dte-ep-asterisk">*</span>}</label>
            <input className="dte-ep-input" placeholder="Field label" value={field.label ?? ''} onChange={e => onPatch({ label: e.target.value })} />
          </>
        )}

        {/* Text: Auto-population + Or enter text + Format */}
        {field.type === 'text' && (
          <>
            <label className="dte-ep-label">Auto-population</label>
            <select className="dte-ep-input" value={field.autoPopulate ?? ''} onChange={e => onPatch({ autoPopulate: e.target.value })}>
              <option value="">Select a value</option>
              {AUTO_POP_OPTIONS.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <label className="dte-ep-label">Or enter text</label>
            <input className="dte-ep-input" value={field.defaultText ?? ''} onChange={e => onPatch({ defaultText: e.target.value })} />
            <label className="dte-ep-label">Format</label>
            <select className="dte-ep-input" value={field.format ?? 'any'} onChange={e => onPatch({ format: e.target.value as any })}>
              <option value="any">Any Value</option>
              <option value="email">Email</option>
              <option value="number">Number</option>
              <option value="phone">Phone</option>
            </select>
          </>
        )}

        {/* Date: Date Format */}
        {field.type === 'date' && (
          <>
            <label className="dte-ep-label">Date Format</label>
            <select className="dte-ep-input" value={field.dateFormat ?? 'MDY'} onChange={e => onPatch({ dateFormat: e.target.value as any })}>
              <option value="MDY">MDY</option>
              <option value="DMY">DMY</option>
              <option value="YMD">YMD</option>
            </select>
          </>
        )}

        {/* Select / Radio: Options */}
        {(field.type === 'select' || field.type === 'radio') && (
          <>
            <div className="dte-ep-options-head">
              <span>Options</span>
              <button className="dte-ep-add" title="Add option" onClick={addOption}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
            </div>
            {options.map((o, i) => (
              <div key={i} className="dte-ep-option">
                <span className="dte-ep-grip"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.3"/><circle cx="15" cy="6" r="1.3"/><circle cx="9" cy="12" r="1.3"/><circle cx="15" cy="12" r="1.3"/><circle cx="9" cy="18" r="1.3"/><circle cx="15" cy="18" r="1.3"/></svg></span>
                <input className="dte-ep-input" value={o} onChange={e => setOption(i, e.target.value)} />
                <button className="dte-ep-opt-remove" title="Remove" onClick={() => removeOption(i)} disabled={options.length <= 1}>×</button>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="dte-ep-foot">
        <button className="dte-ep-delete" onClick={onDelete}>Delete Field</button>
        <button className="dte-ep-done" onClick={onClose}>Done</button>
      </div>
    </div>
  );
};

/* Role edit modal */
const RoleModal: React.FC<{ role: DocRole; canDelete: boolean; onClose: () => void; onSave: (r: DocRole) => void; onDelete: () => void }> = ({ role, canDelete, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(role.name);
  const [color, setColor] = useState(role.color);
  return (
    <div className="dte-modal-backdrop" onClick={onClose}>
      <div className="dte-modal" onClick={e => e.stopPropagation()}>
        <div className="dte-modal-head"><h3>Edit Role</h3><button onClick={onClose}>×</button></div>
        <div className="dte-modal-body">
          <label className="dte-modal-label">Name</label>
          <input className="dte-modal-input" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <label className="dte-modal-label">Color</label>
          <div className="dte-color-row">
            {ROLE_COLORS.map(c => (
              <button key={c} className={`dte-color-swatch ${color === c ? 'sel' : ''}`} style={{ background: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className="dte-modal-foot dte-modal-foot-split">
          {canDelete && <button className="dte-btn-delete" onClick={onDelete}>Delete Role</button>}
          <div className="dte-modal-foot-right">
            <button className="dte-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="dte-btn-save" disabled={!name.trim()} onClick={() => onSave({ ...role, name: name.trim(), color })}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplateEditor;
